import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Globe from "react-globe.gl";
import * as THREE from "three";
import { api, formatNumber } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import JourneyCardModal from "@/components/JourneyCardModal";

const GEO_URL = "/countries.geojson";

export default function ImpactPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, requireAuth } = useAuth();
  const globeRef = useRef();
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [hoverPlace, setHoverPlace] = useState(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [ringPlaces, setRingPlaces] = useState([]);
  const [countries, setCountries] = useState([]);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);

  const globeMaterial = useMemo(
    () => new THREE.MeshPhongMaterial({ color: "#F2F1EC", transparent: false, opacity: 1 }),
    []
  );

  useEffect(() => {
    setReducedMotion(!!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);
    const upd = () => {
      // Reserve space at the bottom for the stats sheet so the globe never
      // renders labels beneath the "Your branch / People reached" band.
      // Mobile portrait needs more room (larger 4-across grid stacks).
      const isMobile = window.innerWidth < 640;
      const reserved = isMobile ? 260 : 200;
      setSize({ w: window.innerWidth, h: Math.max(320, window.innerHeight - reserved) });
    };
    upd(); window.addEventListener("resize", upd);
    return () => window.removeEventListener("resize", upd);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { requireAuth(() => {}); return; }
    api.get(`/messages/${id}`).then((r) => setMsg(r.data.message)).catch(() => {});
    api.get(`/messages/${id}/impact`).then((r) => setData(r.data))
      .catch((e) => {
        if (e?.response?.status === 401) navigate(`/message/${id}`, { replace: true });
      });
    fetch(GEO_URL).then((r) => r.json())
      .then((j) => setCountries(j?.features || []))
      .catch(() => setCountries([]));
    // eslint-disable-next-line
  }, [id, user, authLoading]);

  // Cleanup WebGL on unmount
  useEffect(() => {
    return () => {
      try {
        const g = globeRef.current;
        if (g && typeof g.renderer === "function") {
          const renderer = g.renderer();
          renderer?.dispose?.();
          try { renderer?.forceContextLoss?.(); } catch (_) {}
        }
      } catch (_) {}
      try { globeMaterial.dispose(); } catch (_) {}
    };
  }, [globeMaterial]);

  // Origin zoom-in: start far away, then fly to origin ~2s.
  useEffect(() => {
    if (!globeRef.current || !data) return;
    const g = globeRef.current;
    g.controls().autoRotate = false;
    g.controls().enableZoom = true;
    const o = data.origin;
    if (o?.lat != null && o?.lon != null) {
      // Start view from space
      g.pointOfView({ lat: o.lat, lng: o.lon, altitude: 3.6 }, 0);
      if (reducedMotion) {
        g.pointOfView({ lat: o.lat, lng: o.lon, altitude: 2.4 }, 0);
      } else {
        // After a short beat, zoom smoothly to origin over ~2s
        const t1 = setTimeout(() => {
          g.pointOfView({ lat: o.lat, lng: o.lon, altitude: 1.9 }, 2000);
        }, 250);
        // Then gently pull back and enable slow auto-rotate so arcs are visible
        const t2 = setTimeout(() => {
          g.pointOfView({ lat: o.lat, lng: o.lon, altitude: 2.4 }, 1200);
          g.controls().autoRotate = true;
          g.controls().autoRotateSpeed = 0.28;
        }, 2500);
        return () => { clearTimeout(t1); clearTimeout(t2); };
      }
    }
  }, [data, reducedMotion]);

  // Staggered landing rings (arcs begin after the origin beat)
  useEffect(() => {
    if (!data?.branch?.places) return;
    setRingPlaces([]);
    const start = reducedMotion ? 0 : 2600;
    const timers = data.branch.places.map((p, i) =>
      setTimeout(() => {
        setRingPlaces((prev) => [
          ...prev,
          { lat: p.lat, lng: p.lon, city: p.city, country: p.country,
            reached: p.reached, id: `${p.city}-${i}` },
        ]);
      }, start + (reducedMotion ? 0 : i * 300))
    );
    return () => timers.forEach(clearTimeout);
  }, [data, reducedMotion]);

  if (!data || !msg) {
    return (
      <div className="min-h-screen flex items-center justify-center oh-label">
        Preparing your journey…
      </div>
    );
  }

  const origin = data.origin;
  const originPoint = origin?.lat != null && origin?.lon != null
    ? [{ lat: origin.lat, lng: origin.lon, id: "origin",
         city: origin.city || origin.country, country: origin.country, isOrigin: true }]
    : [];

  const arcs = (data.branch.places || []).map((p) => ({
    startLat: origin.lat, startLng: origin.lon,
    endLat: p.lat, endLng: p.lon,
    color: ["rgba(217,45,32,0.9)", "rgba(217,45,32,0.05)"],
    label: `${p.city.toUpperCase()} · ${p.country.toUpperCase()}`,
  }));

  const points = [
    ...originPoint,
    ...(data.branch.places || []).map((p) => ({
      lat: p.lat, lng: p.lon, id: `${p.city}-${p.country}`,
      city: p.city, country: p.country, reached: p.reached, isOrigin: false,
    })),
  ];

  // Only label the top 6 destinations by reach to keep the globe readable.
  // Also suppress any label whose great-circle distance to the origin is
  // < 1400 km — the origin's "YOUR VOICE STARTED HERE" tag needs breathing
  // room and would otherwise collide with cities across the same continent
  // (e.g. Madrid 1262 km + London 1003 km for an Italian origin still land
  // within screen-space of the origin tag at zoom altitude 2.4).
  const _hav = (aLat, aLon, bLat, bLon) => {
    const R = 6371, toRad = Math.PI / 180;
    const dLat = (bLat - aLat) * toRad, dLon = (bLon - aLon) * toRad;
    const s1 = Math.sin(dLat / 2), s2 = Math.sin(dLon / 2);
    const c = s1 * s1 + Math.cos(aLat * toRad) * Math.cos(bLat * toRad) * s2 * s2;
    return R * 2 * Math.atan2(Math.sqrt(c), Math.sqrt(1 - c));
  };
  const oLat = data.origin?.lat, oLon = data.origin?.lon;
  const topPlaces = [...(data.branch.places || [])]
    .filter((p) => oLat == null || oLon == null || _hav(oLat, oLon, p.lat, p.lon) >= 1400)
    .sort((a, b) => (b.reached || 0) - (a.reached || 0))
    .slice(0, 6);
  const topLabelIds = new Set(topPlaces.map((p) => `${p.city}-${p.country}`));
  const labelsData = points.filter((p) => p.isOrigin || topLabelIds.has(p.id));

  const overflowCountries = Math.max(0, data.branch.countries_count - (data.branch.places?.length || 0));

  return (
    <div className="fixed inset-0 bg-[color:var(--oh-bg)]"
         onMouseMove={(e) => setMouse({ x: e.clientX, y: e.clientY })}>
      <div className="absolute top-0 left-0 right-0"
           style={{ height: size.h, pointerEvents: "auto" }}>
        <Globe
          ref={globeRef}
          width={size.w}
          height={size.h}
          backgroundColor="rgba(0,0,0,0)"
          globeImageUrl={null}
          showGlobe={true}
          showAtmosphere={false}
          globeMaterial={globeMaterial}
          polygonsData={countries}
          polygonAltitude={0.005}
          polygonCapColor={() => "rgba(17,17,17,0.05)"}
          polygonSideColor={() => "rgba(17,17,17,0.02)"}
          polygonStrokeColor={() => "rgba(17,17,17,0.35)"}
          arcsData={arcs}
          arcColor={"color"}
          arcStroke={0.5}
          arcAltitude={0.22}
          arcDashLength={0.5}
          arcDashGap={1.4}
          arcDashAnimateTime={reducedMotion ? 0 : 2400}
          ringsData={ringPlaces}
          ringColor={() => (t) => `rgba(217,45,32,${1 - t})`}
          ringMaxRadius={2.2}
          ringPropagationSpeed={2}
          ringRepeatPeriod={reducedMotion ? 99999 : 1400}
          pointsData={points}
          pointLat={"lat"} pointLng={"lng"}
          pointColor={(d) => d.isOrigin ? "#111111" : "#D92D20"}
          pointAltitude={(d) => d.isOrigin ? 0.02 : 0.008}
          pointRadius={(d) => d.isOrigin ? 1.1 : 0.6}
          onPointHover={setHoverPlace}
          onPointClick={setHoverPlace}
          labelsData={labelsData}
          labelLat={"lat"} labelLng={"lng"}
          labelText={(d) => d.isOrigin
            ? "YOUR VOICE STARTED HERE"
            : `${(d.city || "").toUpperCase()} · ${(d.country || "").toUpperCase()}`}
          labelSize={(d) => d.isOrigin ? 1.4 : 0.95}
          labelDotRadius={0}
          labelColor={(d) => d.isOrigin ? "#111111" : "rgba(17,17,17,0.72)"}
          labelResolution={2}
          labelAltitude={(d) => d.isOrigin ? 0.06 : 0.012}
        />
      </div>

      <button onClick={() => navigate(-1)}
              className="absolute top-6 left-6 oh-label z-10 hover:text-[color:var(--oh-red)]"
              data-testid="impact-back">← Back</button>

      <button onClick={() => setCardOpen(true)}
              className="absolute top-16 left-6 sm:top-16 oh-label z-20 hover:text-[color:var(--oh-red)] border oh-hairline px-4 py-2 bg-[color:var(--oh-bg)]/90 backdrop-blur-sm"
              data-testid="impact-share-card">Share journey card →</button>

      <div className="absolute top-6 right-6 text-right z-10 max-w-[45vw] sm:max-w-xs">
        <div className="oh-label">Your voice travelled</div>
        <div className="font-serif-editorial text-xl sm:text-3xl mt-2 leading-tight" data-testid="impact-msg-title">
          {msg.title}
        </div>
        {data.is_demo_branch && (
          <div className="oh-label mt-3 text-[color:var(--oh-ink-3)]" data-testid="impact-demo-note">
            Demo branch · illustrative
          </div>
        )}
      </div>

      {hoverPlace && (
        <div className="fixed z-20 pointer-events-none bg-[color:var(--oh-bg)] border oh-hairline px-4 py-3 shadow"
             style={{
               left: Math.min(mouse.x + 14, size.w - 260),
               top: Math.min(mouse.y + 14, size.h - 120),
             }}
             data-testid="impact-tooltip">
          <div className="font-serif-editorial text-lg leading-tight">
            {(hoverPlace.city || "").toUpperCase()}, {(hoverPlace.country || "").toUpperCase()}
          </div>
          {hoverPlace.isOrigin ? (
            <div className="oh-label mt-2">Your voice started here</div>
          ) : (
            <div className="oh-label mt-2">
              {formatNumber(hoverPlace.reached)} people reached through your branch
            </div>
          )}
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 z-10 max-h-[52vh] overflow-y-auto border-t oh-hairline bg-[color:var(--oh-bg)]/95 backdrop-blur-sm">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 pt-6 pb-8">
          <div className="oh-label">Your branch</div>
          {origin?.city && (
            <div className="mt-1 oh-label text-[color:var(--oh-ink-3)]" data-testid="impact-origin">
              Started here · {origin.city}
              {origin.region ? ` (${origin.region})` : ""}, {origin.country}
            </div>
          )}
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-6">
            <Stat label="People reached" v={data.branch.people_reached} tid="impact-reached" />
            <Stat label="Voices generated" v={data.branch.voices_generated} tid="impact-voices" />
            <Stat label="Countries" v={data.branch.countries_count} tid="impact-countries" />
            <Stat label="Human generations" v={data.branch.human_generations} tid="impact-generations" />
          </div>

          {data.branch.places?.length > 0 && (
            <div className="mt-8 border-t oh-hairline pt-6">
              <div className="oh-label" data-testid="impact-journey-title">Key places your branch reached</div>
              <div className="mt-3 flex flex-wrap gap-x-3 gap-y-1 font-serif-editorial text-lg leading-snug"
                   data-testid="impact-journey">
                {data.branch.places.slice(0, 12).map((p, i) => (
                  <span key={`${p.city}-${p.country}-${i}`}>
                    {p.city}, {p.country}
                    {i < Math.min(data.branch.places.length, 12) - 1 && (
                      <span className="text-[color:var(--oh-ink-3)] px-1">→</span>
                    )}
                  </span>
                ))}
                {overflowCountries > 0 && (
                  <span className="oh-label ml-2 self-center">+ {overflowCountries} more countries</span>
                )}
              </div>
            </div>
          )}

          <div className="mt-6 pt-4 border-t oh-hairline flex flex-wrap justify-between items-baseline gap-4">
            <div className="oh-label">
              Whole message · {formatNumber(data.global.voices)} voices · {data.global.countries} countries
            </div>
            {data.branch.longest_reach_km > 0 && (
              <div className="oh-label" data-testid="impact-human-distance">
                Human distance · {formatNumber(data.branch.longest_reach_km)} km
              </div>
            )}
          </div>
        </div>
      </div>

      <JourneyCardModal open={cardOpen} onOpenChange={setCardOpen} message={msg} />
    </div>
  );
}

function Stat({ label, v, tid }) {
  return (
    <div>
      <div className="oh-label">{label}</div>
      <div className="text-3xl sm:text-5xl font-light mt-2 tabular" data-testid={tid}>
        {(Number(v) || 0).toLocaleString("en-US")}
      </div>
    </div>
  );
}
